import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark,
    faDownload,
    faSpinner,
    faClockRotateLeft,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from '../../components/FlashMessage';
import { extractApiError } from '../../utils/errorMessages';
import { fetchReportRuns, downloadPastRun } from '../../services/reportingService';
import { formatDateTime, formatCell } from './formatCell';

/**
 * Every file this person has already downloaded from ONE report.
 *
 * A kept file holds the rows its own runner was allowed to see, so the server
 * only ever lists a person their own downloads. A file too big to keep is still
 * listed — the record of what was handed over matters even when the bytes are
 * gone — it simply has no "Download again" button.
 *
 * `refreshKey` changes after a fresh download in the viewer, which re-loads the
 * list so the file that was just saved is in it.
 */
const PastDownloadsDrawer = ({ reportId, reportName, refreshKey = 0, onClose }) => {
    const { showError, showSuccess } = useFlashMessage();
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchReportRuns(reportId);
            setRuns(data.runs || []);
        } catch (err) {
            setError(extractApiError(err, 'Could not load the past downloads for this report.'));
        } finally {
            setLoading(false);
        }
    }, [reportId]);

    useEffect(() => {
        load();
    }, [load, refreshKey]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const handleDownloadAgain = async (run) => {
        setBusyId(run.id);
        try {
            const fileName = await downloadPastRun(run);
            showSuccess(`Saved ${fileName}.`);
        } catch (err) {
            showError(extractApiError(err, 'Could not open that download again.'));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div
            className="rc-drawer-overlay"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <aside className="rc-drawer" role="dialog" aria-modal="true" aria-label="Past downloads">
                <div className="rc-drawer-head">
                    <div>
                        <div className="rc-drawer-eyebrow">
                            <FontAwesomeIcon icon={faClockRotateLeft} /> Past downloads
                        </div>
                        <h2 className="rc-drawer-title">{reportName}</h2>
                    </div>
                    <button type="button" className="rc-icon-btn" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="rc-drawer-body">
                    {loading ? (
                        <div className="rc-drawer-note">Loading past downloads…</div>
                    ) : error ? (
                        <div className="rc-error">
                            <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
                        </div>
                    ) : runs.length === 0 ? (
                        <div className="rc-drawer-note">
                            You have not downloaded this report yet. Every file you download is
                            listed here so you can open the same one again.
                        </div>
                    ) : (
                        <ul className="rc-run-list">
                            {runs.map((run) => (
                                <li key={run.id} className="rc-run-row">
                                    <div className="rc-run-main">
                                        <div className="rc-run-name">{run.file_name}</div>
                                        <div className="rc-run-meta">
                                            {formatDateTime(run.created_at)}
                                            {' · '}
                                            {formatCell(run.row_count, 'integer')} rows
                                            {run.run_by ? ` · ${run.run_by}` : ''}
                                        </div>
                                    </div>
                                    {run.is_stored ? (
                                        <button
                                            type="button"
                                            className="rc-btn-ghost"
                                            onClick={() => handleDownloadAgain(run)}
                                            disabled={busyId === run.id}
                                        >
                                            <FontAwesomeIcon
                                                icon={busyId === run.id ? faSpinner : faDownload}
                                                spin={busyId === run.id}
                                            />
                                            {busyId === run.id ? 'Opening…' : 'Download again'}
                                        </button>
                                    ) : (
                                        <span className="rc-run-gone">Too large to keep</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </aside>
        </div>
    );
};

export default PastDownloadsDrawer;
